import React, { Key, useEffect } from 'react';
import { useAsyncList } from '@react-stately/data';
import { MenuTriggerAction } from '@react-types/combobox';
import { LoadingState } from '@react-types/shared';
import cn from 'classnames';

import { Country, signal } from '@nw/shared/config';

import { useQueryClientCountriesHook } from '../../hooks/useQueryClientCountriesHook';
import { useStateAndDistricts } from '../../hooks/useStateAndDistricts';
import {
  getCountryIdFromCountryName,
  getCountryNameFromCountryId,
  getFilteredCountries
} from '../../utils/ComboBoxUtils';

import { ComboBox } from '../ComboboxWithInitialValue';
import { AsyncListLoadFunction, OptionsType, OverlayOptions } from '../types';

export interface CountriesComboBoxProps {
  label: string;
  options?: Array<OptionsType>;
  onSelectionChange?: (e: Key) => void;
  selectedKey?: string;
  placeholder?: string;
  renderListItem?: any;
  onBlur?: (e: React.FocusEvent) => void;
  inputValue?: string;
  onInputChange?: (value: string) => void;
  onOpenChange?: (
    isOpen: boolean,
    menuTrigger?: MenuTriggerAction | undefined
  ) => void;
  error?: string;
  fieldContainerStyles?: string;
  wrapperWithPopOverStyles?: string;
  inputGroupStyles?: string;
  inputFieldStyles?: string;
  buttonStyles?: string;
  errorMessageStyles?: string;
  popOverStyles?: string;
  listBoxStyles?: string;
  dropDownIcon?: any;
  selectedOptionIcon?: any;
  defaultFilter?: any;
  isDisabled?: boolean;
  apiStatus?: string;
  autoFocus?: boolean;
  listItemStyles?: string;
  itemContentStyles?: string;
  loadingState?: LoadingState;
  onLoadMore?: () => any;
  overlayOptions?: OverlayOptions;
  getListItemTextValue?: (T: any) => any;
  getDisplayValueFromSelectedCountry?: (T: any) => string;
  helperText?: string;
  hintClassName?: string;
  enableDataMask?: boolean;
  initialCountryValue?: string;
}

export const CountriesComboBox = (props: CountriesComboBoxProps) => {
  const {
    onOpenChange,
    onInputChange,
    onSelectionChange,
    selectedKey,
    inputValue,
    inputFieldStyles = '',
    buttonStyles = '',
    placeholder = '',
    error,
    getDisplayValueFromSelectedCountry: getDisplayValueFromSelectedState,
    helperText,
    hintClassName,
    enableDataMask,
    initialCountryValue,
    ...others
  } = props;
  const { countries } = useStateAndDistricts();

  const onSelectionChangeFunc = (key) => {
    onSelectionChange && onSelectionChange(key);
  };

  const onOpenChangeFunc = (key: any) => {
    onOpenChange && onOpenChange(key);
  };

  const onInputChangeFunc = (value) => {
    onInputChange && onInputChange(value);
  };

  const countriesDataPromise = useQueryClientCountriesHook(signal);

  const loadItems: AsyncListLoadFunction<
    { country_id: string; country_name: any },
    any
  > = async ({ signal, cursor, filterText, selectedKeys, items }) => {
    const json = await countriesDataPromise;

    const selectedKey = (selectedKeys as Set<React.Key>).values().next().value;

    countries.current = json.countries;

    const filteredCountries = Boolean(initialCountryValue && !selectedKey)
      ? json.countries
      : getFilteredCountries(filterText, json.countries);

    return {
      items: filteredCountries,
      cursor: null,
      filterText: filterText
    };
  };

  useEffect(() => {
    list.removeSelectedItems();
    list.setFilterText(''); //NOTE: load function in useAsyncList is called when setFilterText is called
  }, []);

  const list = useAsyncList({
    load: loadItems,
    initialFilterText: initialCountryValue ?? '',
    initialSelectedKeys: [],
    getKey: (item: Country) => item.country_id
  });

  const getInputTextFromValue = (value, list) => {
    return getCountryNameFromCountryId(value, list);
  };

  const getValueFromInputText = (text, list) => {
    return getCountryIdFromCountryName(text, list);
  };

  return (
    <ComboBox
      {...others}
      selectedKey={selectedKey}
      onSelectionChange={onSelectionChangeFunc}
      onInputChange={onInputChangeFunc}
      onOpenChange={onOpenChangeFunc}
      inputFieldStyles={cn('w-full', inputFieldStyles)}
      buttonStyles={buttonStyles}
      getListItemKey={(item) => item.country_id}
      placeholder={placeholder}
      getListItemTextValue={(item) => item.country_name}
      renderListItem={({ item }) => (
        <p key={item.country_id}>{item.country_name}</p>
      )}
      error={error}
      initialValue={initialCountryValue}
      getInputTextFromValue={getInputTextFromValue}
      getValueFromInputText={getValueFromInputText}
      list={list}
      getDisplayValueFromSelectedState={getDisplayValueFromSelectedState}
      helperText={helperText}
      hintClassName={hintClassName}
      enableDataMask={enableDataMask}
    />
  );
};
